var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { computed, ref } from 'vue';
import { httpService, langService, reportService, toastService } from '@/core';
import { userUpdateUrl } from '@/api/user';
const DEFAULT_THEME = 'light';
const THEME_OPTIONS = ['light', 'dark', 'system'];
const STORAGE_KEY = 'theme';
function normalizeTheme(value) {
    return THEME_OPTIONS.includes(value) ? value : DEFAULT_THEME;
}
// The database is the source of truth, but pages rendered before the user is known
// (login, logout) only have the localStorage mirror to go by, so it is kept in sync.
function storeTheme(value) {
    try {
        localStorage.setItem(STORAGE_KEY, value);
    }
    catch (_a) {
        // Storage can be unavailable (private mode, blocked cookies) — the theme still applies.
    }
}
// The server renders the resolved preference into the document head so the correct theme
// is painted before the app boots. The composable starts from that same value.
function readInitialTheme() {
    var _a;
    const value = (_a = document.querySelector('meta[name="theme-preference"]')) === null || _a === void 0 ? void 0 : _a.getAttribute('content');
    if (THEME_OPTIONS.includes(value)) {
        return value;
    }
    try {
        return normalizeTheme(localStorage.getItem(STORAGE_KEY));
    }
    catch (_b) {
        return DEFAULT_THEME;
    }
}
const theme = ref(readInitialTheme());
function applyTheme() {
    const value = theme.value;
    storeTheme(value);
    if (value === 'dark' || (value === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
        document.documentElement.setAttribute('data-theme', 'dark');
    }
    else {
        document.documentElement.removeAttribute('data-theme');
    }
}
export function useTheme() {
    // Lazy: the composable is also used before translations are loaded (auth boot).
    const themeOptions = computed(() => THEME_OPTIONS.map(option => ({
        id: option,
        title: langService.trans(`profile.theme.${option}`),
    })));
    /**
     * Apply a theme that already comes from the server (e.g. on user boot), without
     * writing it back.
     */
    function syncTheme(value) {
        theme.value = normalizeTheme(value);
        applyTheme();
    }
    function setTheme(value) {
        return __awaiter(this, void 0, void 0, function* () {
            const previous = theme.value;
            if (previous === value) {
                return;
            }
            theme.value = value;
            applyTheme();
            try {
                yield httpService.patch(userUpdateUrl() + '?update_theme=true', {
                    data: { theme: value },
                });
            }
            catch (e) {
                theme.value = previous;
                applyTheme();
                toastService.fromResponse(e);
                reportService.report(e);
            }
        });
    }
    return { theme, setTheme, syncTheme, themeOptions, applyTheme };
}
